/**
 * The background music's mixing logic: one looping theme from the start
 * screen through the whole run, with its volume set by where the player is.
 * Pure apart from what is passed in (the audio element factory, timers,
 * storage, the document), so the tests can drive it with mocks.
 */

// The start screen's level (unchanged from v2's menu theme).
export const MENU_VOLUME = 0.4;
// In play the theme sits at 40% of the menu level (20*log10(0.4) = -8 dB):
// far enough under the barks, catches and the clock to leave them on top,
// still loud enough to read as the same track carrying on.
export const GAME_VOLUME = MENU_VOLUME * 0.4;
// A dip halves the in-game level again (-6 dB), for the moments that have
// their own sound: the bus drop-off, LEVEL COMPLETE, the TIRED rest.
export const DUCK_FACTOR = 0.5;

export const LEVEL_VOLUMES = {menu: MENU_VOLUME, game: GAME_VOLUME, out: 0};

// Fade lengths in ms, on the wall clock.
export const FADES = {
  game: 1000, // START: menu level down to the in-game level
  menu: 1500, // the end panel or the start screen: back up to the menu level
  out: 600, // the ending starts: out, so the champ or loser sound lands
  duckDown: 250,
  duckUp: 600,
  handOff: 400 // the start screen after a reload, picking the track up again
};

export const STORAGE_KEY = 'receipthunt.music';
export const HANDOFF_KEY = 'receipthunt.musicAt';
// a hand-off older than this is from another visit, not a reload
const HANDOFF_MAX_MS = 15000;
const FADE_STEP_MS = 25;

/**
 * musicMix
 * What the music should be doing for a state: playing or not, and at what
 * volume. The menu toggle (off) wins over everything and stops the track; the
 * game's mute silences it but lets it run on, like the other sounds; the game
 * pause and a hidden tab hold it where it is.
 * @param {Object} s
 * @param {Boolean} s.started play() or setLevel() has been called
 * @param {Boolean} s.off the start screen's music toggle is off
 * @param {Boolean} s.gameMuted the game's mute (M)
 * @param {Boolean} s.paused the game's pause (P)
 * @param {Boolean} s.hidden the tab is hidden
 * @param {String} s.level 'menu' | 'game' | 'out'
 * @param {Number} s.ducks how many dips are on
 * @returns {{play: Boolean, volume: Number}}
 */
export function musicMix(s) {
  const play = !!s.started && !s.off && !s.paused && !s.hidden;
  if (s.off || s.gameMuted) {
    return {play, volume: 0};
  }
  let volume = LEVEL_VOLUMES[s.level] || 0;
  if (s.level === 'game' && s.ducks > 0) {
    volume *= DUCK_FACTOR;
  }
  return {play, volume};
}

/**
 * MusicController
 * Owns the one Audio element. Browsers refuse to play sound until the page
 * has been interacted with, so a blocked start waits for the first click or
 * key press. The toggle is remembered in localStorage (this browser only).
 */
export class MusicController {
  /**
   * @param {Object} deps
   * @param {Function} deps.createAudio () => HTMLAudioElement-like
   * @param {Object} [deps.doc] document-like: add/removeEventListener, hidden
   * @param {Object} [deps.storage] localStorage-like (may throw)
   * @param {Object} [deps.session] sessionStorage-like (may throw)
   * @param {Function} [deps.gainRoute] (audio) => {set(v), resume()} | null,
   *   for browsers whose media volume is read-only (iOS)
   * @param {Function} [deps.now] ms clock
   * @param {Function} [deps.setInterval]
   * @param {Function} [deps.clearInterval]
   */
  constructor(deps) {
    this.deps = deps;
    this.doc = deps.doc || null;
    this.now = deps.now || (() => Date.now());
    this.setTimer = deps.setInterval || ((fn, ms) => setInterval(fn, ms));
    this.clearTimer = deps.clearInterval || ((id) => clearInterval(id));
    this.audio = null;
    this.route = null;
    this.muted = this.readMuted(); // the start screen's toggle, kept by name for MenuSfx
    this.started = false;
    this.level = 'menu';
    this.ducks = new Set();
    this.gameMuted = false;
    this.paused = false;
    this.hidden = !!(this.doc && this.doc.hidden);
    this.volume = MENU_VOLUME; // what is applied now
    this.fade = null;
    this.waiting = false;
    this.unlock = this.unlock.bind(this);
    if (this.doc) {
      this.doc.addEventListener('visibilitychange', () => {
        this.hidden = !!this.doc.hidden;
        this.update(0);
      });
    }
  }

  readMuted() {
    try {
      return this.deps.storage.getItem(STORAGE_KEY) === 'off';
    } catch (e) {
      return false; // storage unavailable: default to music on
    }
  }

  saveMuted() {
    try {
      this.deps.storage.setItem(STORAGE_KEY, this.muted ? 'off' : 'on');
    } catch (e) {
      // private browsing: the toggle still works for this visit
    }
  }

  /**
   * state
   * @returns {Object} the inputs to musicMix
   */
  state() {
    return {
      started: this.started,
      off: this.muted,
      gameMuted: this.gameMuted,
      paused: this.paused,
      hidden: this.hidden,
      level: this.level,
      ducks: this.ducks.size
    };
  }

  /**
   * target
   * @returns {{play: Boolean, volume: Number}}
   */
  target() {
    return musicMix(this.state());
  }

  element() {
    if (!this.audio) {
      const audio = this.deps.createAudio();
      audio.loop = true;
      audio.preload = 'auto';
      this.audio = audio;
      this.route = this.deps.gainRoute ? this.deps.gainRoute(audio) : null;
      this.apply(this.volume);
      this.pickUp(audio);
    }
    return this.audio;
  }

  /**
   * pickUp
   * The game goes home and Play again by reloading the page; handOff() left
   * the track's position, so the theme carries on rather than restarting,
   * fading back in from silence.
   * @param {Object} audio
   */
  pickUp(audio) {
    let saved = null;
    try {
      saved = JSON.parse(this.deps.session.getItem(HANDOFF_KEY) || 'null');
      this.deps.session.removeItem(HANDOFF_KEY);
    } catch (e) {
      saved = null;
    }
    if (!saved || typeof saved.t !== 'number' || !(this.now() - saved.at < HANDOFF_MAX_MS)) {
      return;
    }
    const seek = () => {
      const t = saved.t + (this.now() - saved.at) / 1000;
      const d = audio.duration;
      try {
        audio.currentTime = d > 0 && isFinite(d) ? t % d : t;
      } catch (e) {
        // not seekable yet: start from the top
      }
    };
    if (audio.readyState >= 1 || !audio.addEventListener) {
      seek();
    } else {
      audio.addEventListener('loadedmetadata', seek, {once: true});
    }
    this.volume = 0;
    this.apply(0);
    this.pickedUp = true;
  }

  /**
   * handOff
   * Leaves the track's position for the next page (see pickUp).
   */
  handOff() {
    // a paused track (the game pause) still hands its place on
    if (!this.audio || !this.started || this.muted) {
      return;
    }
    try {
      this.deps.session.setItem(HANDOFF_KEY, JSON.stringify({t: this.audio.currentTime, at: this.now()}));
    } catch (e) {
      // no storage: the next page starts the theme from the top
    }
  }

  apply(v) {
    this.volume = v;
    if (this.route) {
      this.route.set(v);
    } else if (this.audio) {
      this.audio.volume = Math.max(0, Math.min(1, v));
    }
  }

  /**
   * fadeTo
   * Moves the volume to `to` over `ms` of wall-clock time.
   * @param {Number} to
   * @param {Number} ms
   */
  fadeTo(to, ms) {
    if (this.fade !== null) {
      this.clearTimer(this.fade);
      this.fade = null;
    }
    const from = this.volume;
    if (!ms || from === to) {
      this.apply(to);
      return;
    }
    const t0 = this.now();
    this.fade = this.setTimer(() => {
      const k = Math.min(1, (this.now() - t0) / ms);
      this.apply(from + (to - from) * k);
      if (k >= 1) {
        this.clearTimer(this.fade);
        this.fade = null;
      }
    }, FADE_STEP_MS);
  }

  /**
   * update
   * Brings the element in line with the state.
   * @param {Number} ms fade length for any volume change
   */
  update(ms) {
    const mix = this.target();
    if (mix.play) {
      this.start();
    } else if (this.audio && !this.audio.paused) {
      this.audio.pause();
    }
    if (this.pickedUp && mix.play) {
      // the first start after a reload fades in from where pickUp left it
      this.pickedUp = false;
      ms = Math.max(ms, FADES.handOff);
    }
    this.fadeTo(mix.volume, ms);
  }

  start() {
    const audio = this.element();
    if (!audio.paused) {
      return;
    }
    let attempt;
    try {
      attempt = audio.play();
    } catch (e) {
      attempt = null;
      this.waitForInteraction();
    }
    if (attempt && attempt.catch) {
      attempt.catch(() => this.waitForInteraction());
    }
    if (this.route && this.route.resume) {
      this.waitForInteraction(); // a Web Audio route also starts suspended
    }
  }

  waitForInteraction() {
    if (this.waiting || !this.doc) {
      return;
    }
    this.waiting = true;
    this.doc.addEventListener('pointerdown', this.unlock, true);
    this.doc.addEventListener('keydown', this.unlock, true);
  }

  unlock(event) {
    this.waiting = false;
    this.doc.removeEventListener('pointerdown', this.unlock, true);
    this.doc.removeEventListener('keydown', this.unlock, true);
    if (this.route && this.route.resume) {
      this.route.resume();
    }
    // if the first thing pressed is the music toggle, don't blip the music on first
    if (event && event.target && event.target.closest && event.target.closest('.music-toggle')) {
      return;
    }
    if (this.target().play && this.audio && this.audio.paused) {
      const attempt = this.audio.play();
      if (attempt && attempt.catch) {
        attempt.catch(() => {});
      }
    }
  }

  /**
   * play
   * The start screen: the theme at the menu level, or waiting for the first
   * interaction. Starting it again keeps its place.
   */
  play() {
    this.started = true;
    const wasLevel = this.level;
    this.level = 'menu';
    this.update(wasLevel === 'menu' ? 0 : FADES.menu);
  }

  /**
   * setLevel
   * @param {String} level 'menu' | 'game' | 'out'
   */
  setLevel(level) {
    if (level === this.level && this.started) {
      return;
    }
    this.started = true;
    this.level = level;
    if (level !== 'game') {
      this.ducks.clear();
    }
    this.update(FADES[level]);
  }

  /** fadeOut: the ending starts. */
  fadeOut() {
    this.setLevel('out');
  }

  /**
   * duck
   * A short dip under a moment with its own sound. Dips nest by reason: the
   * music comes back up only when every reason has been released.
   * @param {Boolean} on
   * @param {String} [reason='default']
   */
  duck(on, reason = 'default') {
    const had = this.ducks.size;
    if (on) {
      this.ducks.add(reason);
    } else {
      this.ducks.delete(reason);
    }
    if (had === this.ducks.size) {
      return;
    }
    this.update(this.ducks.size > had ? FADES.duckDown : FADES.duckUp);
  }

  /**
   * setGameMuted
   * The game's mute (M) takes the music with every other sound.
   * @param {Boolean} muted
   */
  setGameMuted(muted) {
    this.gameMuted = !!muted;
    this.update(0);
  }

  /**
   * setPaused
   * The game's pause (P) holds the track where it is.
   * @param {Boolean} paused
   */
  setPaused(paused) {
    this.paused = !!paused;
    this.update(0);
  }

  /**
   * toggle
   * The start screen's music switch, remembered for the game too.
   * @returns {Boolean} true when the music is off afterwards
   */
  toggle() {
    this.muted = !this.muted;
    this.saveMuted();
    this.update(0);
    return this.muted;
  }
}
